/**
 * PIBulkEditor Component
 * Beautiful Airtable-style spreadsheet editor for bulk NPI PI data entry
 * Supports copy/paste, drag-fill, dropdowns, and keyboard navigation
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { 
  ModuleRegistry, 
  AllCommunityModule,
  type ColDef, 
  type ColGroupDef,
  type ValueFormatterParams,
  type ValueParserParams,
  type CellValueChangedEvent,
  type GridReadyEvent,
  type GridApi,
  type FillOperationParams,
  type ICellRendererParams
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Save, 
  Loader2, 
  CheckCircle2, 
  AlertTriangle,
  RotateCcw,
  Download,
  Info,
  HelpCircle,
  Check,
  X,
  Hash,
  List,
  Calendar,
  FileText,
  ChevronDown
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  bulkUploadPIData,
  calculatePIScoreClient,
  type PICalculatorData 
} from '@/lib/pi-calculator-utils';
import { getProviders } from '@/lib/measure-outcomes';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

interface PIBulkEditorProps {
  practiceId: string;
  onSaveComplete?: () => void;
}

// Row data type for the grid
interface PIGridRow extends PICalculatorData {
  id: string;
  providerName?: string;
  calculatedScore?: number;
  isDirty?: boolean;
}

// Custom cell renderer for Yes/No fields
function YesNoCellRenderer(params: ICellRendererParams) {
  const value = params.value;
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
        <Check className="w-3 h-3" /> Yes
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        <X className="w-3 h-3" /> No
      </span>
    );
  }
  return <span className="text-slate-400 text-xs">—</span>;
}

// Custom cell renderer for Score
function ScoreCellRenderer(params: ICellRendererParams) {
  const value = params.value;
  if (value == null) return <span className="text-slate-400">—</span>;
  
  const score = Math.round(value);
  let colorClass = 'bg-red-100 text-red-700';
  if (score >= 90) colorClass = 'bg-emerald-100 text-emerald-700';
  else if (score >= 75) colorClass = 'bg-blue-100 text-blue-700';
  else if (score >= 50) colorClass = 'bg-amber-100 text-amber-700';
  
  return (
    <span className={cn('inline-flex items-center px-2.5 py-1 rounded-md text-sm font-bold', colorClass)}>
      {score}%
    </span>
  );
}

// Custom cell renderer for numbers
function NumberCellRenderer(params: ICellRendererParams) {
  const value = params.value;
  if (value == null || value === '') return <span className="text-slate-400 text-sm">—</span>;
  return <span className="font-mono text-sm">{value.toLocaleString()}</span>;
}

// Custom header component with description
function CustomHeader(props: any) {
  const { displayName, column } = props;
  const description = column.getColDef().headerTooltip;
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div className="flex flex-col items-start justify-center h-full py-1 cursor-help">
            <span className="text-xs font-semibold text-slate-700 leading-tight">
              {displayName}
            </span>
            {description && (
              <HelpCircle className="w-3 h-3 text-slate-400 mt-0.5" />
            )}
          </div>
        </TooltipTrigger>
        {description && (
          <TooltipContent side="bottom" className="max-w-[280px] p-3">
            <p className="text-sm">{description}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

// Styles for Airtable-like appearance
const gridStyles = `
  .pi-bulk-editor .ag-root-wrapper {
    border: none !important;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .pi-bulk-editor .ag-header {
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 2px solid #e2e8f0;
  }
  
  .pi-bulk-editor .ag-header-row {
    height: 48px !important;
  }
  
  .pi-bulk-editor .ag-header-group-cell {
    background: linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%);
    border-right: 1px solid #cbd5e1;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #475569;
  }
  
  .pi-bulk-editor .ag-header-cell {
    border-right: 1px solid #e2e8f0;
    padding: 0 12px;
  }
  
  .pi-bulk-editor .ag-header-cell-label {
    justify-content: flex-start;
  }
  
  .pi-bulk-editor .ag-row {
    border-bottom: 1px solid #f1f5f9;
  }
  
  .pi-bulk-editor .ag-row:hover {
    background-color: #f8fafc;
  }
  
  .pi-bulk-editor .ag-row-even {
    background-color: #ffffff;
  }
  
  .pi-bulk-editor .ag-row-odd {
    background-color: #fafbfc;
  }
  
  .pi-bulk-editor .ag-cell {
    border-right: 1px solid #f1f5f9;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    font-size: 13px;
  }
  
  .pi-bulk-editor .ag-cell-focus {
    border: 2px solid #3b82f6 !important;
    background-color: #eff6ff !important;
  }
  
  .pi-bulk-editor .ag-cell-inline-editing {
    padding: 4px 8px;
  }
  
  .pi-bulk-editor .ag-select {
    height: 32px;
    font-size: 13px;
  }
  
  .pi-bulk-editor .ag-input-field-input {
    font-size: 13px;
    padding: 6px 10px;
  }
  
  /* Column group colors */
  .pi-bulk-editor .ag-header-group-cell[col-id="provider-group"] {
    background: linear-gradient(180deg, #e0e7ff 0%, #c7d2fe 100%);
    color: #3730a3;
  }
  
  .pi-bulk-editor .ag-header-group-cell[col-id="prereq-group"] {
    background: linear-gradient(180deg, #fef3c7 0%, #fde68a 100%);
    color: #92400e;
  }
  
  .pi-bulk-editor .ag-header-group-cell[col-id="erx-group"] {
    background: linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1e40af;
  }
  
  .pi-bulk-editor .ag-header-group-cell[col-id="patient-group"] {
    background: linear-gradient(180deg, #d1fae5 0%, #a7f3d0 100%);
    color: #065f46;
  }
  
  .pi-bulk-editor .ag-header-group-cell[col-id="hie-group"] {
    background: linear-gradient(180deg, #ede9fe 0%, #ddd6fe 100%);
    color: #5b21b6;
  }
  
  .pi-bulk-editor .ag-header-group-cell[col-id="ph-group"] {
    background: linear-gradient(180deg, #fce7f3 0%, #fbcfe8 100%);
    color: #9d174d;
  }
  
  /* Pinned columns */
  .pi-bulk-editor .ag-pinned-left-header,
  .pi-bulk-editor .ag-pinned-left-cols-container {
    box-shadow: 4px 0 8px -4px rgba(0,0,0,0.1);
  }
`;

export function PIBulkEditor({ practiceId, onSaveComplete }: PIBulkEditorProps) {
  const { programYear } = useProgramYear();
  const { toast } = useToast();
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [rowData, setRowData] = useState<PIGridRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveResult, setSaveResult] = useState<{ success: number; errors: number } | null>(null);
  const [showGuide, setShowGuide] = useState(true);

  // Boolean value formatter/parser for Yes/No display
  const booleanFormatter = (params: ValueFormatterParams): string => {
    if (params.value === true) return 'Yes';
    if (params.value === false) return 'No';
    return '';
  };

  const booleanParser = (params: ValueParserParams): boolean | null => {
    const val = String(params.newValue).toLowerCase().trim();
    if (val === 'yes' || val === 'true' || val === 'y' || val === '1') return true;
    if (val === 'no' || val === 'false' || val === 'n' || val === '0') return false;
    return null;
  };

  // Level formatter/parser
  const levelFormatter = (params: ValueFormatterParams): string => {
    if (params.value === 'PROD') return 'Production';
    if (params.value === 'PRE') return 'Pre-production';
    return '';
  };

  const levelParser = (params: ValueParserParams): string | null => {
    const val = String(params.newValue).toLowerCase().trim();
    if (val.includes('prod') || val === 'production') return 'PROD';
    if (val.includes('pre')) return 'PRE';
    return null;
  };

  // Common cell class for Yes/No fields
  const yesNoCellClass = 'text-center';
  
  // Yes/No column factory
  const createYesNoColumn = (field: string, headerName: string, tooltip: string, width = 100): ColDef => ({
    field,
    headerName,
    headerTooltip: tooltip,
    width,
    editable: true,
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: { values: ['Yes', 'No', ''] },
    valueFormatter: booleanFormatter,
    valueParser: booleanParser,
    cellRenderer: YesNoCellRenderer,
    cellClass: yesNoCellClass,
  });

  // Number column factory
  const createNumberColumn = (field: string, headerName: string, tooltip: string, width = 100): ColDef => ({
    field,
    headerName,
    headerTooltip: tooltip,
    width,
    editable: true,
    cellEditor: 'agNumberCellEditor',
    valueParser: (params) => params.newValue === '' ? null : Number(params.newValue),
    cellRenderer: NumberCellRenderer,
  });

  // Column definitions with groups
  const columnDefs: (ColDef | ColGroupDef)[] = useMemo(() => [
    // Provider Info Group (Pinned)
    {
      headerName: '📋 Provider',
      groupId: 'provider-group',
      children: [
        {
          field: 'providerName',
          headerName: 'Name',
          headerTooltip: 'Provider name from your practice roster',
          pinned: 'left',
          width: 160,
          editable: false,
          cellStyle: { fontWeight: '500' },
        },
        {
          field: 'npi',
          headerName: 'NPI',
          headerTooltip: 'National Provider Identifier',
          pinned: 'left',
          width: 110,
          editable: false,
          cellStyle: { fontFamily: 'monospace', fontSize: '12px', color: '#64748b' },
        },
        {
          field: 'calculatedScore',
          headerName: 'Score',
          headerTooltip: 'Calculated PI score based on entered data',
          pinned: 'left',
          width: 80,
          editable: false,
          cellRenderer: ScoreCellRenderer,
        },
      ],
    },

    // Reporting Period
    {
      headerName: '📅 Reporting Period',
      children: [
        {
          field: 'performance_start',
          headerName: 'Start Date',
          headerTooltip: 'Performance period start (YYYY-MM-DD)',
          width: 120,
          editable: true,
        },
        {
          field: 'performance_end',
          headerName: 'End Date',
          headerTooltip: 'Performance period end (YYYY-MM-DD)',
          width: 120,
          editable: true,
        },
        {
          field: 'cehrt_id',
          headerName: 'CEHRT ID',
          headerTooltip: 'Certified EHR Technology ID (e.g., CEHRT123456789)',
          width: 140,
          editable: true,
        },
      ],
    },

    // Prerequisites Group
    {
      headerName: '⚠️ Prerequisites (Required)',
      groupId: 'prereq-group',
      children: [
        createYesNoColumn('pi_pphi_1', 'Security Risk', 'Did you conduct or review a security risk analysis of your certified EHR technology?', 110),
        createYesNoColumn('pi_pphi_2', 'SAFER Guides', 'Did you use SAFER Guides to assess and address safety concerns?', 110),
        createYesNoColumn('pi_infblo_1', 'No Blocking', 'Do you attest to not engaging in information blocking practices?', 100),
        createYesNoColumn('pi_oncdir_1', 'ONC Review', 'Do you acknowledge ONC Direct Review requirements?', 100),
      ],
    },

    // e-Prescribing Group
    {
      headerName: '💊 e-Prescribing (20 pts)',
      groupId: 'erx-group',
      children: [
        createNumberColumn('pi_ep_1_numerator', 'e-Rx Num', 'Number of prescriptions written and transmitted electronically'),
        createNumberColumn('pi_ep_1_denominator', 'e-Rx Denom', 'Total number of prescriptions written'),
        createYesNoColumn('pi_lvpp_1', 'Exclusion?', 'Claim exclusion if you wrote no prescriptions requiring e-prescribing', 95),
        createYesNoColumn('pi_ep_2', 'Query PDMP', 'Did you query PDMP for controlled substances?', 100),
        {
          field: 'pi_ep_2_exclusion',
          headerName: 'PDMP Excl.',
          headerTooltip: 'Exclusion reason: State/Jurisdiction ban or No Schedule II drugs',
          width: 120,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ['', 'State/Jurisdiction', 'No Schedule II'] },
        },
      ],
    },

    // Patient Access Group
    {
      headerName: '👤 Patient Access (25 pts)',
      groupId: 'patient-group',
      children: [
        createNumberColumn('pi_pea_1_numerator', 'Access Num', 'Patients provided timely access to health info'),
        createNumberColumn('pi_pea_1_denominator', 'Access Denom', 'Total unique patients seen'),
      ],
    },

    // HIE Group
    {
      headerName: '🔄 Health Info Exchange (30 pts)',
      groupId: 'hie-group',
      children: [
        {
          field: 'hie_option',
          headerName: 'HIE Option',
          headerTooltip: 'Choose how you report HIE: Option 1 (Send+Receive), Option 2 (Bi-directional), or Option 3 (TEFCA)',
          width: 130,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ['', '1 - Send/Receive', '2 - Bi-directional', '3 - TEFCA'] },
          valueFormatter: (params) => {
            if (params.value === 1) return '1 - Send/Receive';
            if (params.value === 2) return '2 - Bi-directional';
            if (params.value === 3) return '3 - TEFCA';
            return '';
          },
          valueParser: (params) => {
            const val = String(params.newValue);
            if (val.startsWith('1')) return 1;
            if (val.startsWith('2')) return 2;
            if (val.startsWith('3')) return 3;
            return null;
          },
        },
        createNumberColumn('pi_hie_1_numerator', 'Send Num', 'Electronic referrals/transitions sent', 95),
        createNumberColumn('pi_hie_1_denominator', 'Send Denom', 'Total referrals/transitions', 100),
        createYesNoColumn('pi_lvotc_1', 'Send Excl?', 'Exclusion for sending', 95),
        createNumberColumn('pi_hie_4_numerator', 'Recv Num', 'Records reconciled from external sources', 95),
        createNumberColumn('pi_hie_4_denominator', 'Recv Denom', 'Total transitions of care received', 100),
        createYesNoColumn('pi_lvitc_2', 'Recv Excl?', 'Exclusion for receiving', 95),
        createYesNoColumn('pi_hie_5', 'Bi-Direct', 'Enabled bi-directional exchange (Option 2)', 95),
        createYesNoColumn('pi_hie_6', 'TEFCA', 'Participate in TEFCA (Option 3)', 85),
      ],
    },

    // Public Health Group
    {
      headerName: '🏥 Public Health (25 pts + 5 bonus)',
      groupId: 'ph-group',
      children: [
        createYesNoColumn('pi_phcdrr_1', 'Immunization', 'Report to Immunization Registry (required)', 105),
        {
          field: 'pi_phcdrr_1_level',
          headerName: 'Imm Level',
          headerTooltip: 'Reporting level: Production (active) or Pre-production (testing)',
          width: 100,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ['', 'Production', 'Pre-production'] },
          valueFormatter: levelFormatter,
          valueParser: levelParser,
        },
        createYesNoColumn('pi_phcdrr_2', 'Syndromic', 'Report to Syndromic Surveillance (bonus)', 95),
        {
          field: 'pi_phcdrr_2_level',
          headerName: 'Synd Level',
          headerTooltip: 'Syndromic surveillance reporting level',
          width: 100,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ['', 'Production', 'Pre-production'] },
          valueFormatter: levelFormatter,
          valueParser: levelParser,
        },
        createYesNoColumn('pi_phcdrr_4', 'PH Registry', 'Report to Public Health Registry (bonus)', 100),
        {
          field: 'pi_phcdrr_4_level',
          headerName: 'PH Level',
          headerTooltip: 'Public health registry reporting level',
          width: 90,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ['', 'Production', 'Pre-production'] },
          valueFormatter: levelFormatter,
          valueParser: levelParser,
        },
        createYesNoColumn('pi_phcdrr_5', 'Clinical Reg', 'Report to Clinical Data Registry (bonus)', 100),
        {
          field: 'pi_phcdrr_5_level',
          headerName: 'CDR Level',
          headerTooltip: 'Clinical data registry reporting level',
          width: 90,
          editable: true,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ['', 'Production', 'Pre-production'] },
          valueFormatter: levelFormatter,
          valueParser: levelParser,
        },
      ],
    },
  ], []);

  // Default column settings
  const defaultColDef: ColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false,
    enableCellChangeFlash: true,
    headerComponent: CustomHeader,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  }), []);

  // Load providers and existing data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const providers = await getProviders(practiceId);
        
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        const token = localStorage.getItem('auth_token');
        
        const response = await fetch(`${apiUrl}/api/practices/${practiceId}/pi-calculator/all-npis?year=${programYear}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        let existingData: Record<string, PICalculatorData> = {};
        if (response.ok) {
          const data = await response.json();
          existingData = data.reduce((acc: Record<string, PICalculatorData>, item: any) => {
            acc[item.npi] = item;
            return acc;
          }, {});
        }

        const rows: PIGridRow[] = providers.map(provider => {
          const existing = existingData[provider.npi] || {};
          const score = calculatePIScoreClient(existing);
          
          return {
            id: provider.npi,
            npi: provider.npi,
            providerName: provider.name || `Provider ${provider.npi}`,
            calculatedScore: score.totalScore,
            ...existing,
            isDirty: false,
          };
        });

        setRowData(rows);
      } catch (error) {
        console.error('Error loading data:', error);
        toast({
          title: 'Error loading data',
          description: 'Failed to load provider data. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [practiceId, toast]);

  // Handle cell value changes
  const onCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    const { data } = event;
    
    data.isDirty = true;
    
    const score = calculatePIScoreClient(data);
    data.calculatedScore = score.totalScore;
    
    event.api.refreshCells({
      rowNodes: [event.node!],
      columns: ['calculatedScore'],
      force: true,
    });
    
    setHasChanges(true);
    setSaveResult(null);
  }, []);

  // Fill operation for drag-fill
  const fillOperation = useCallback((params: FillOperationParams) => {
    return params.currentCellValue;
  }, []);

  // Handle grid ready
  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
  }, []);

  // Save all changes
  const handleSave = async () => {
    if (!rowData.length) return;
    
    setIsSaving(true);
    setSaveResult(null);
    
    try {
      const dirtyRows = rowData.filter(row => row.isDirty);
      
      if (dirtyRows.length === 0) {
        toast({ title: 'No changes', description: 'No changes to save.' });
        setIsSaving(false);
        return;
      }
      
      const dataToUpload: PICalculatorData[] = dirtyRows.map(row => ({
        npi: row.npi,
        performance_start: row.performance_start,
        performance_end: row.performance_end,
        cehrt_id: row.cehrt_id,
        pi_pphi_1: row.pi_pphi_1,
        pi_pphi_2: row.pi_pphi_2,
        pi_infblo_1: row.pi_infblo_1,
        pi_oncdir_1: row.pi_oncdir_1,
        pi_ep_1_numerator: row.pi_ep_1_numerator,
        pi_ep_1_denominator: row.pi_ep_1_denominator,
        pi_lvpp_1: row.pi_lvpp_1,
        pi_ep_2: row.pi_ep_2,
        pi_ep_2_exclusion: row.pi_ep_2_exclusion,
        pi_pea_1_numerator: row.pi_pea_1_numerator,
        pi_pea_1_denominator: row.pi_pea_1_denominator,
        hie_option: row.hie_option,
        pi_hie_1_numerator: row.pi_hie_1_numerator,
        pi_hie_1_denominator: row.pi_hie_1_denominator,
        pi_lvotc_1: row.pi_lvotc_1,
        pi_hie_4_numerator: row.pi_hie_4_numerator,
        pi_hie_4_denominator: row.pi_hie_4_denominator,
        pi_lvitc_2: row.pi_lvitc_2,
        pi_hie_5: row.pi_hie_5,
        pi_hie_6: row.pi_hie_6,
        pi_phcdrr_1: row.pi_phcdrr_1,
        pi_phcdrr_1_level: row.pi_phcdrr_1_level,
        pi_phcdrr_1_exclusion: row.pi_phcdrr_1_exclusion,
        pi_phcdrr_2: row.pi_phcdrr_2,
        pi_phcdrr_2_level: row.pi_phcdrr_2_level,
        pi_phcdrr_4: row.pi_phcdrr_4,
        pi_phcdrr_4_level: row.pi_phcdrr_4_level,
        pi_phcdrr_5: row.pi_phcdrr_5,
        pi_phcdrr_5_level: row.pi_phcdrr_5_level,
      }));
      
      const result = await bulkUploadPIData(practiceId, dataToUpload, programYear);
      
      if (result) {
        setSaveResult({ success: result.processed - result.errors, errors: result.errors });
        
        if (result.errors === 0) {
          setRowData(prev => prev.map(row => ({ ...row, isDirty: false })));
          setHasChanges(false);
          toast({ title: 'Saved successfully', description: `Updated PI data for ${result.processed} providers.` });
          onSaveComplete?.();
        } else {
          toast({ title: 'Partial save', description: `Saved ${result.processed - result.errors} of ${result.processed} providers.`, variant: 'destructive' });
        }
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast({ title: 'Save failed', description: 'Failed to save changes. Please try again.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // Reset all changes
  const handleReset = () => {
    if (!hasChanges) return;
    if (window.confirm('Are you sure you want to discard all unsaved changes?')) {
      window.location.reload();
    }
  };

  // Export to CSV
  const handleExport = () => {
    gridApi?.exportDataAsCsv({
      fileName: `pi_data_${practiceId}_${new Date().toISOString().split('T')[0]}.csv`,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground">Loading provider data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <style>{gridStyles}</style>
      
      {/* Field Guide - Collapsible */}
      <Collapsible open={showGuide} onOpenChange={setShowGuide}>
        <Card className="border-slate-200 bg-slate-50/50">
          <CollapsibleTrigger className="w-full">
            <CardHeader className="py-3 px-4 cursor-pointer hover:bg-slate-100/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-slate-500" />
                  <CardTitle className="text-sm font-medium text-slate-700">
                    How to use this editor
                  </CardTitle>
                </div>
                <ChevronDown className={cn(
                  "w-4 h-4 text-slate-500 transition-transform",
                  showGuide && "rotate-180"
                )} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 pb-4 px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-slate-600 font-medium">
                    <div className="w-4 h-4 rounded bg-emerald-100 flex items-center justify-center">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </div>
                    Yes / No Fields
                  </div>
                  <p className="text-slate-500 pl-6">Click to select from dropdown</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-slate-600 font-medium">
                    <div className="w-4 h-4 rounded bg-blue-100 flex items-center justify-center">
                      <Hash className="w-3 h-3 text-blue-600" />
                    </div>
                    Number Fields
                  </div>
                  <p className="text-slate-500 pl-6">Type a number (whole numbers)</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-slate-600 font-medium">
                    <div className="w-4 h-4 rounded bg-purple-100 flex items-center justify-center">
                      <List className="w-3 h-3 text-purple-600" />
                    </div>
                    Dropdown Fields
                  </div>
                  <p className="text-slate-500 pl-6">Click to select an option</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-slate-600 font-medium">
                    <div className="w-4 h-4 rounded bg-orange-100 flex items-center justify-center">
                      <Calendar className="w-3 h-3 text-orange-600" />
                    </div>
                    Date Fields
                  </div>
                  <p className="text-slate-500 pl-6">Format: YYYY-MM-DD</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-4 text-xs text-slate-500">
                <span><kbd className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-mono">Click</kbd> cell to edit</span>
                <span><kbd className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-mono">Ctrl+C/V</kbd> copy/paste</span>
                <span><kbd className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-mono">Tab</kbd> next cell</span>
                <span><kbd className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-mono">Enter</kbd> confirm</span>
                <span><kbd className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-mono">Esc</kbd> cancel</span>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Toolbar */}
      <div className="flex items-center justify-between bg-white border rounded-lg px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700">
            {rowData.length} providers
          </span>
          {hasChanges && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Unsaved changes
            </Badge>
          )}
          {saveResult && (
            <Badge 
              variant="outline" 
              className={saveResult.errors === 0 
                ? 'text-emerald-600 border-emerald-300 bg-emerald-50' 
                : 'text-red-600 border-red-300 bg-red-50'
              }
            >
              {saveResult.errors === 0 ? (
                <><CheckCircle2 className="w-3 h-3 mr-1" /> Saved {saveResult.success}</>
              ) : (
                <><AlertTriangle className="w-3 h-3 mr-1" /> {saveResult.errors} errors</>
              )}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export all data to CSV file</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleReset}
            disabled={!hasChanges || isSaving}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          
          <Button 
            size="sm" 
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Changes</>
            )}
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div 
        className="pi-bulk-editor ag-theme-alpine border rounded-lg shadow-sm overflow-hidden"
        style={{ height: 520 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          fillOperation={fillOperation}
          enableRangeSelection={true}
          enableFillHandle={true}
          undoRedoCellEditing={true}
          undoRedoCellEditingLimit={20}
          suppressCopyRowsToClipboard={true}
          clipboardDelimiter="\t"
          rowSelection="multiple"
          animateRows={true}
          tooltipShowDelay={300}
          getRowId={(params) => params.data.id}
          rowHeight={44}
          headerHeight={48}
          groupHeaderHeight={36}
        />
      </div>

      {/* Hover over column headers for field descriptions */}
      <p className="text-xs text-center text-slate-400">
        💡 Hover over column headers for detailed field descriptions
      </p>
    </div>
  );
}
